import type {
  AssistantDialogMessage,
  ThreadMessage,
} from "@/shared/components/SupportThread";
import type { SupportRequestShowQueryType } from "@/types/graphql/admin.generated";

type Request = NonNullable<SupportRequestShowQueryType["supportRequest"]>;

const nameOf = (
  p: { displayName?: string | null; email?: string | null } | null | undefined
) => (p ? p.displayName || p.email || null : null);

/** Сообщения ленты из сгенерированных типов в плоский формат общей ленты. */
export function toAdminThreadMessages(
  nodes: Request["messages"]
): ThreadMessage[] {
  return nodes.map((m) => ({
    id: m.id,
    author: `${m.author}`,
    channel: `${m.channel}`,
    body: m.body,
    internal: m.internal,
    viaAssistant: m.viaAssistant,
    event: m.event ? `${m.event}` : null,
    staffName: nameOf(m.staff),
    eventUserName: nameOf(m.eventUser),
    fromEmail: m.fromEmail ?? null,
    attachments: m.attachments.map((a) => ({ ...a })),
    skippedAttachments: m.skippedAttachments,
    hasOriginal: m.hasOriginal,
    createdAt: String(m.createdAt),
  }));
}

/** Реплики зрителя и ответы помощника; отказы, ошибки и служебное скрыты. */
export function toAdminDialogMessages(
  nodes: Request["assistantMessages"]
): AssistantDialogMessage[] {
  return nodes
    .filter((m) => `${m.kind}` === "TEXT")
    .map((m) => ({
      id: m.id,
      role: `${m.role}`,
      text: m.text,
      createdAt: String(m.createdAt),
    }));
}

/**
 * После ответа и смены статуса: карточка, список, счётчик в меню и карточка
 * «Открытые обращения» на дашборде (счётчик сам опрашивается раз в минуту).
 */
export const REQUEST_REFETCH_QUERIES = [
  "SupportRequestShow",
  "SupportRequestsList",
  "SupportRequestsOpenCount",
  "OpenSupportRequests",
] as const;

/** Опрос открытой карточки: ответ зрителя письмом появляется без переоткрытия. */
export const REQUEST_CARD_POLL_MS = 30_000;

// Порядок важен: ASSIGNEE_NOT_FOUND раньше NOT_FOUND, совпадение по подстроке.
// Тексты те же, что у партнёрского кабинета (requests.errors в сиде).
const ERROR_TEXTS = {
  ACTIVE_REQUEST_EXISTS: "По этому платежу уже есть другое активное обращение",
  STATUS_TRANSITION_NOT_ALLOWED: "Этот переход статуса недоступен",
  REQUEST_CHANGED: "Обращение изменилось, обновите карточку и попробуйте снова",
  EMPTY_MESSAGE: "Напишите текст ответа",
  MESSAGE_TOO_LONG: "Слишком длинный ответ",
  ASSIGNEE_NOT_FOUND: "Этого сотрудника нельзя назначить",
  REQUEST_ALREADY_OPEN: "Обращение уже открыто",
  ATTACHMENT_HOST_NOT_ALLOWED: "Вложение с этого адреса загрузить нельзя",
  FORBIDDEN: "Недостаточно прав для этого действия",
  NOT_FOUND: "Обращение не найдено, обновите страницу",
} as const;

const UNKNOWN_ERROR_TEXT = "Не удалось сохранить, попробуйте ещё раз";

interface GraphQLErrorLike {
  message?: string;
  extensions?: { code?: unknown } | null;
}

/**
 * Код ошибки бэкенда (текст UserInputError/CodedError или extensions.code,
 * как у FORBIDDEN с текстом «Forbidden») в понятный текст; сырой текст на
 * экран не идёт.
 */
export function requestErrorText(error: {
  message?: string;
  graphQLErrors?: readonly (GraphQLErrorLike | null | undefined)[];
}): string {
  const codes = [
    ...(error.graphQLErrors ?? []).flatMap((e) => [
      e?.message ?? "",
      typeof e?.extensions?.code === "string" ? e.extensions.code : "",
    ]),
    error.message ?? "",
  ];
  for (const [code, text] of Object.entries(ERROR_TEXTS)) {
    if (codes.some((m) => m.includes(code))) return text;
  }
  return UNKNOWN_ERROR_TEXT;
}
